import type { TypedDocumentNode } from '@apollo/client';
import { useMutation, useQuery } from '@apollo/client/react';
import { APOLLO_FETCH_POLICY } from '@/constants/apollo';
import {
  LINK_I4_ACCOUNT,
  LINK_ILRONLINE_ACCOUNT,
  UNLINK_I4_ACCOUNT,
} from './mutations';
import {
  GET_I4_LINK_STATUS,
  GET_I4_REDIRECT_URL,
  GET_I4_DOWNLOADS,
  GET_I4_LINK_URL,
  GET_I4_LINKS,
} from './queries';
import type {
  UserI4LinkDto,
  DocumentDto,
  LinkI4AccountInput,
  LinkIlrOnlineAccountInput,
  UnlinkI4AccountInput,
  I4RedirectUrlQueryInput,
  I4LinkUrlQueryInput,
} from './types';

/** Headers (X-Society-Id, X-Organisation-Id) come from Apollo societyContextLink reading usePublicSearchContext. */

export function useLinkI4Account() {
  return useMutation(
    LINK_I4_ACCOUNT as TypedDocumentNode<
      { linkI4Account: UserI4LinkDto },
      { input: LinkI4AccountInput }
    >,
  );
}

export function useLinkIlrOnlineAccount() {
  return useMutation(
    LINK_ILRONLINE_ACCOUNT as TypedDocumentNode<
      { linkIlrOnlineAccount: UserI4LinkDto },
      { input: LinkIlrOnlineAccountInput }
    >,
  );
}

export function useUnlinkI4Account() {
  return useMutation(
    UNLINK_I4_ACCOUNT as TypedDocumentNode<
      { unlinkI4Account: boolean },
      { input: UnlinkI4AccountInput }
    >,
  );
}

export function useI4LinkStatus(societyCode: string, skip = false) {
  return useQuery(
    GET_I4_LINK_STATUS as TypedDocumentNode<
      { i4LinkStatus: UserI4LinkDto | null },
      { societyCode: string }
    >,
    {
      variables: { societyCode },
      skip: skip || !societyCode,
      fetchPolicy: APOLLO_FETCH_POLICY.NETWORK_ONLY,
    },
  );
}

export function useI4RedirectUrl(input: I4RedirectUrlQueryInput, skip = false) {
  return useQuery(
    GET_I4_REDIRECT_URL as TypedDocumentNode<
      { i4RedirectUrl: string },
      { input: I4RedirectUrlQueryInput }
    >,
    {
      variables: { input },
      skip: skip || !input.societyCode,
      fetchPolicy: APOLLO_FETCH_POLICY.NETWORK_ONLY,
    },
  );
}

export function useI4Downloads(societyCode: string, skip = false) {
  return useQuery(
    GET_I4_DOWNLOADS as TypedDocumentNode<
      { i4Downloads: DocumentDto[] },
      { societyCode: string }
    >,
    {
      variables: { societyCode },
      skip: skip || !societyCode,
      fetchPolicy: APOLLO_FETCH_POLICY.NETWORK_ONLY,
    },
  );
}

export function useI4LinkUrl(input: I4LinkUrlQueryInput, skip = false) {
  return useQuery(
    GET_I4_LINK_URL as TypedDocumentNode<
      { i4LinkUrl: string },
      { input: I4LinkUrlQueryInput }
    >,
    {
      variables: { input },
      skip: skip || !input.societyCode || !input.callbackUrl,
      // Use cache so prefetch from Downloads/Online Reg makes Start button instant on link-i4
      fetchPolicy: APOLLO_FETCH_POLICY.CACHE_FIRST,
    },
  );
}

export function useI4Links(skip = false) {
  return useQuery(
    GET_I4_LINKS as TypedDocumentNode<{ i4Links: UserI4LinkDto[] }>,
    {
      skip,
      fetchPolicy: APOLLO_FETCH_POLICY.NETWORK_ONLY,
    },
  );
}
